const XML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

const escapeXmlText = (s: string): string =>
  s.replace(/[&<>]/g, (c) => XML_ESCAPE[c]);

const escapeXmlAttr = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => XML_ESCAPE[c]);

// nodeType numéricos para no depender del global `Node` (jsdom/tests)
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const CDATA_NODE = 4;
const PI_NODE = 7;
const COMMENT_NODE = 8;

function parseXmlDocument(xml: string): Document | null {
  if (typeof DOMParser === "undefined") return null;
  try {
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    return doc.getElementsByTagName("parsererror").length > 0 ? null : doc;
  } catch {
    return null;
  }
}

const serializeAttrs = (el: Element): string =>
  Array.from(el.attributes)
    .map((a) => ` ${a.name}="${escapeXmlAttr(a.value)}"`)
    .join("");

function serializeNode(node: ChildNode, depth: number, out: string[]): void {
  const pad = "  ".repeat(depth);
  switch (node.nodeType) {
    case ELEMENT_NODE: {
      const el = node as Element;
      const open = `<${el.tagName}${serializeAttrs(el)}`;
      const children = Array.from(el.childNodes).filter(
        (n) => n.nodeType !== TEXT_NODE || (n.nodeValue || "").trim() !== "",
      );
      if (children.length === 0) {
        out.push(`${pad}${open}/>`);
        return;
      }
      if (children.length === 1 && children[0].nodeType === TEXT_NODE) {
        const text = (children[0].nodeValue || "").trim();
        const embedded = formatEmbeddedXml(text, depth + 1);
        if (embedded) {
          out.push(`${pad}${open}>`, ...embedded, `${pad}</${el.tagName}>`);
        } else {
          out.push(`${pad}${open}>${escapeXmlText(text)}</${el.tagName}>`);
        }
        return;
      }
      out.push(`${pad}${open}>`);
      for (const child of children) serializeNode(child, depth + 1, out);
      out.push(`${pad}</${el.tagName}>`);
      return;
    }
    case TEXT_NODE: {
      const text = (node.nodeValue || "").trim();
      if (text) out.push(pad + escapeXmlText(text));
      return;
    }
    case CDATA_NODE:
      out.push(`${pad}<![CDATA[${node.nodeValue || ""}]]>`);
      return;
    case COMMENT_NODE:
      out.push(`${pad}<!--${node.nodeValue || ""}-->`);
      return;
    case PI_NODE: {
      const pi = node as ProcessingInstruction;
      out.push(`${pad}<?${pi.target} ${pi.data}?>`);
      return;
    }
  }
}

function formatEmbeddedXml(text: string, depth: number): string[] | null {
  if (!text.startsWith("<") || !text.endsWith(">")) return null;
  if (!text.includes("</") && !text.includes("/>")) return null;
  const doc = parseXmlDocument(text);
  if (!doc) return null;
  const lines: string[] = [];
  doc.childNodes.forEach((n) => serializeNode(n, depth, lines));
  return lines.map((line) => escapeXmlText(line));
}

function formatXmlFallback(xml: string): string {
  const withBreaks = xml.replace(/>\s*</g, ">\n<");
  let pad = 0;
  return withBreaks
    .split("\n")
    .map((raw) => {
      const node = raw.trim();
      if (node.startsWith("</")) pad = Math.max(pad - 1, 0);
      const line = "  ".repeat(pad) + node;
      const opensBlock =
        node.startsWith("<") &&
        node.endsWith(">") &&
        !node.startsWith("</") &&
        !node.startsWith("<?") &&
        !node.startsWith("<!") &&
        !node.endsWith("/>") &&
        !node.includes("</");
      if (opensBlock) pad += 1;
      return line;
    })
    .join("\n");
}

export function formatXml(xml: string): string {
  const trimmed = (xml || "").trim();
  if (!trimmed) return "";

  const doc = parseXmlDocument(trimmed);
  if (!doc) return formatXmlFallback(trimmed);

  const out: string[] = [];
  const decl = trimmed.match(/^<\?xml[\s\S]*?\?>/);
  if (decl) out.push(decl[0]);
  doc.childNodes.forEach((n) => serializeNode(n, 0, out));
  return out.length > 0 ? out.join("\n") : trimmed;
}

function tryFormatJson(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") {
      const inner = parsed.trim();
      if (inner.startsWith("<")) return formatXml(inner);
      try {
        return JSON.stringify(JSON.parse(inner), null, 2);
      } catch {
      }
    }
    return JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
}

export function formatPayload(raw: string): string {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("<")) return formatXml(trimmed);
  return tryFormatJson(trimmed) ?? trimmed;
}

export interface PayloadFileMeta {
  ext: "xml" | "json" | "txt";
  mime: string;
}

export function payloadFileMeta(raw: string): PayloadFileMeta {
  const trimmed = (raw || "").trim();
  if (trimmed.startsWith("<")) return { ext: "xml", mime: "application/xml" };
  try {
    JSON.parse(trimmed);
    return { ext: "json", mime: "application/json" };
  } catch {
    return { ext: "txt", mime: "text/plain" };
  }
}

export function formatTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString();
}

export function triggerDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try {
      document.body.removeChild(a);
    } catch {
      /* noop */
    }
    URL.revokeObjectURL(url);
  }, 300);
}

export function downloadText(
  content: string,
  filename: string,
  mime: string
): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
}

export async function screenshotPayload(
  title: string,
  subtitle: string,
  content: string,
  filename: string
): Promise<void> {
  const PADDING = 32;
  const FONT_SIZE = 13;
  const LINE_HEIGHT = Math.round(FONT_SIZE * 1.6);
  const HEADER_HEIGHT = 80;
  const MAX_WIDTH = 1100;
  const SCALE = 2;
  const BG = "#1e293b";
  const BODY_FG = "#e2e8f0";
  const TITLE_FG = "#f8fafc";
  const SUB_FG = "#94a3b8";
  const DIVIDER = "#475569";
  const FONT_MONO = `${FONT_SIZE}px Consolas, "Courier New", monospace`;

  const meas = document.createElement("canvas").getContext("2d");
  if (!meas) throw new Error("Canvas no disponible");
  meas.font = FONT_MONO;
  const innerWidth = MAX_WIDTH - PADDING * 2;

  const charWidths = new Map<string, number>();
  const charWidth = (ch: string): number => {
    let w = charWidths.get(ch);
    if (w === undefined) {
      w = meas.measureText(ch).width;
      charWidths.set(ch, w);
    }
    return w;
  };

  const wrapped: string[] = [];
  for (const raw of content.split("\n")) {
    if (raw.length === 0) {
      wrapped.push("");
      continue;
    }
    let cur = "";
    let curWidth = 0;
    for (const ch of raw) {
      const w = charWidth(ch);
      if (cur && curWidth + w > innerWidth) {
        wrapped.push(cur);
        cur = ch;
        curWidth = w;
      } else {
        cur += ch;
        curWidth += w;
      }
    }
    wrapped.push(cur);
  }

  const MAX_LINES = 600;
  if (wrapped.length > MAX_LINES) {
    const hidden = wrapped.length - MAX_LINES;
    wrapped.length = MAX_LINES;
    wrapped.push(`… (+${hidden} líneas truncadas)`);
  }

  const totalHeight = HEADER_HEIGHT + wrapped.length * LINE_HEIGHT + PADDING;

  const canvas = document.createElement("canvas");
  canvas.width = MAX_WIDTH * SCALE;
  canvas.height = totalHeight * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context no disponible");
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, MAX_WIDTH, totalHeight);

  ctx.textBaseline = "top";
  ctx.fillStyle = TITLE_FG;
  ctx.font = `bold 16px Inter, system-ui, sans-serif`;
  ctx.fillText(title, PADDING, PADDING);

  ctx.fillStyle = SUB_FG;
  ctx.font = `12px Inter, system-ui, sans-serif`;
  ctx.fillText(subtitle, PADDING, PADDING + 22);

  ctx.strokeStyle = DIVIDER;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PADDING, HEADER_HEIGHT - 10);
  ctx.lineTo(MAX_WIDTH - PADDING, HEADER_HEIGHT - 10);
  ctx.stroke();

  ctx.fillStyle = BODY_FG;
  ctx.font = FONT_MONO;
  let y = HEADER_HEIGHT;
  for (const line of wrapped) {
    if (line.length > 0) ctx.fillText(line, PADDING, y);
    y += LINE_HEIGHT;
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png")
  );
  if (!blob) throw new Error("No se pudo generar el PNG");
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
}

export function buildFileName(
  ev: {
    integrator_adapter: string;
    imei?: string;
    device_id?: string;
    sent_at: string;
  },
  field: string,
  ext: string
): string {
  const ts = (ev.sent_at || new Date().toISOString())
    .slice(0, 19)
    .replace(/[:T]/g, "-");
  const id = ev.imei || ev.device_id || ev.integrator_adapter || "evidence";
  return `${id}_${field.toUpperCase()}_${ts}.${ext}`;
}
